import { fetchBuildInfo, isStaleBuild } from "~/utils/buildVersion";

/**
 * The tab's view of "am I still running the deployed build?".
 *
 * State only. Nothing here reloads the page on its own — a reload is always the
 * user's click on the banner, because there is no way for this code to know
 * what it would be taking away: a live call the other participants are
 * watching, a KBA answer, a signature half drawn. What it can do is say what it
 * knows, and let the person decide when.
 *
 * The polling and the error listeners that set this live in
 * `plugins/10-app-update.client.ts`; the banner that reads it is
 * `CommonAppUpdateBanner`.
 *
 * @see utils/buildVersion for how staleness is decided.
 */

/**
 * One tab, one answer — module scope rather than `useState`, which is safe
 * because the app never renders on a server. The plugin writes these and the
 * banner reads them, from wherever each of them runs.
 */
/** A newer build is live; the tab is running code that is on its way out. */
const updateAvailable = ref(false);
/**
 * An import has already failed, so something on screen is dead rather than
 * merely out of date. The difference is what the user is being told: one is a
 * suggestion they can ignore until they are between things, the other explains
 * why a feature just stopped working — so this one is worth saying mid-session.
 */
const chunkFailed = ref(false);
/** The build the deploy is serving, once a check has seen it. */
const deployedBuildId = ref("");
/** Keeps a burst of focus and visibility events down to one network trip. */
const lastCheckedAt = ref(0);

export const useAppUpdate = () => {
  const config = useRuntimeConfig();
  const runningBuildId = String(config.public?.buildId ?? "");

  const markUpdateAvailable = (buildId = "") => {
    deployedBuildId.value = buildId;
    updateAvailable.value = true;
  };

  /**
   * An import failed: the deploy moved while this tab was open, and whatever
   * needed that chunk is now broken. Raises the banner with the wording that
   * says so, rather than the one that suggests updating.
   */
  const markChunkFailed = () => {
    chunkFailed.value = true;
    updateAvailable.value = true;
  };

  /**
   * Compares the running build against the deployed one.
   *
   * @returns whether the tab is now known to be stale
   */
  const checkForUpdate = async (): Promise<boolean> => {
    lastCheckedAt.value = Date.now();

    const info = await fetchBuildInfo();
    if (!info) return updateAvailable.value;

    if (isStaleBuild(runningBuildId, info.buildId)) {
      markUpdateAvailable(info.buildId);
    }

    return updateAvailable.value;
  };

  /** Only ever reached from the banner's button. */
  const reloadForUpdate = () => {
    globalThis.location?.reload();
  };

  return {
    runningBuildId,
    deployedBuildId,
    updateAvailable,
    chunkFailed,
    lastCheckedAt,
    checkForUpdate,
    markUpdateAvailable,
    markChunkFailed,
    reloadForUpdate,
  };
};
